import React, { useEffect, useState } from "react";
import { Dashboard } from "../lib/api";
import { SampleBadge } from "../components/common/SampleBadge";
import { useLang } from "../context/LanguageContext";
import { PageHeader } from "../components/common/PageHeader";
import { StatusBadge } from "../components/common/StatusBadge";
import { sampleLoans, sampleRepayments, sampleRecoveryTrend } from "../lib/sampleData";
import { formatINRCompact, formatINR } from "../lib/format";
import {
  Building2, Home, Users, Banknote, TrendingUp, CheckCircle2,
  Scale, AlertTriangle, ArrowUpRight,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";

const KpiCard = ({ label, sublabel, value, trend, icon: Icon, warning = false, testId }) => (
  <div
    data-testid={testId}
    className={`group relative overflow-hidden rounded-2xl border p-5 transition-all hover:shadow-md ${
      warning
        ? "border-rose-300/70 bg-gradient-to-br from-rose-50 to-white"
        : "border-emerald-950/10 bg-white"
    }`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${
        warning ? "bg-rose-100 text-rose-700" : "bg-emerald-900/8 text-emerald-800"
      }`}>
        <Icon className="h-5 w-5" strokeWidth={2.2} />
      </div>
      <ArrowUpRight className="h-4 w-4 text-emerald-900/20 transition-colors group-hover:text-emerald-900/60" />
    </div>
    <div className="mt-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-900/60">
        {label}
      </div>
      <div className={`mt-1.5 font-mono-tabular text-2xl font-bold tracking-tight sm:text-3xl ${
        warning ? "text-rose-700" : "text-emerald-950"
      }`}>
        {value}
      </div>
      <div className="mt-1 text-[11px] text-emerald-900/50">{sublabel}</div>
    </div>
    <div className={`mt-3 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
      warning ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"
    }`}>
      {trend}
    </div>
  </div>
);

const DashboardPage = () => {
  const { t } = useLang();
  const [s, setS] = useState(null);
  useEffect(() => { Dashboard.stats().then(setS).catch(() => setS({})); }, []);
  const n = (k) => (s ? (s[k] ?? 0) : "…");

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title={t.dashboard.welcomeTitle}
        subtitle={t.dashboard.welcomeSub}
        testId="dashboard-page-header"
        showSampleBadge={false}
      />

      <div className="mb-5 rounded-xl border border-emerald-900/15 bg-emerald-50/60 px-4 py-3 text-sm text-emerald-900" data-testid="dashboard-live-note">
        Counts, loan totals, collections, outstanding and overdue below are <strong>live from your database</strong>.
        The chart and EMI table further down are <strong>sample data</strong>.
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard testId="kpi-total-clfs" icon={Building2} label={t.kpi.totalClfs} sublabel="Active CLFs" value={n("total_clfs")} trend="Live" />
        <KpiCard testId="kpi-total-vos" icon={Home} label={t.kpi.totalVos} sublabel={t.kpi.totalVosSub} value={n("total_vos")} trend="Live" />
        <KpiCard testId="kpi-total-shgs" icon={Users} label={t.kpi.totalShgs} sublabel={t.kpi.totalShgsSub} value={n("total_shgs")} trend={`${n("total_members")} members`} />
        <KpiCard testId="kpi-active-loans" icon={Banknote} label={t.kpi.activeLoans} sublabel="Status: active" value={n("active_loans")} trend="Live" />
        <KpiCard testId="kpi-total-disbursed" icon={TrendingUp} label={t.kpi.totalDisbursed} sublabel="Active + closed loans" value={s ? formatINRCompact(s.total_disbursed || 0) : "…"} trend="Live" />
        <KpiCard testId="kpi-total-collected" icon={CheckCircle2} label={t.kpi.totalCollected} sublabel="Confirmed repayments" value={s ? formatINRCompact(s.total_collected || 0) : "…"} trend="Live" />
        <KpiCard testId="kpi-outstanding-balance" icon={Scale} label={t.kpi.outstanding} sublabel="Principal + interest due, active loans" value={s ? formatINRCompact(s.outstanding_total || 0) : "…"} trend="Live" />
        <KpiCard testId="kpi-overdue-amount" icon={AlertTriangle} label={t.kpi.overdue} sublabel={`${s?.overdue_loans ?? 0} loan(s) with overdue instalments`} value={s ? formatINRCompact(s.overdue_amount || 0) : "…"} trend="Live" warning={!!s?.overdue_amount} />
      </div>

      {/* Chart + Recent Loans */}
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-emerald-950/10 bg-white p-5 lg:col-span-2" data-testid="recovery-trend-card">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <h3 className="font-display text-base font-semibold text-emerald-950">
                {t.dashboard.recoveryTrend} <SampleBadge className="ml-1 align-middle" />
              </h3>
              <p className="text-xs text-emerald-900/50">Last 6 months · Lakhs (₹)</p>
            </div>
          </div>
          <div className="h-64 w-full">
            <ResponsiveContainer>
              <LineChart data={sampleRecoveryTrend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,76,58,0.08)" />
                <XAxis dataKey="month" stroke="#0F4C3A" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis stroke="#0F4C3A" fontSize={11} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{
                    background: "white",
                    border: "1px solid rgba(15,76,58,0.15)",
                    borderRadius: 12,
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="disbursed" stroke="#0F4C3A" strokeWidth={2.5} dot={{ r: 3 }} name="Disbursed (L)" />
                <Line type="monotone" dataKey="collected" stroke="#D95D39" strokeWidth={2.5} dot={{ r: 3 }} name="Collected (L)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-emerald-950/10 bg-white p-5" data-testid="recent-loans-card">
          <h3 className="font-display text-base font-semibold text-emerald-950">
            {t.dashboard.recentLoans} <SampleBadge className="ml-1 align-middle" />
          </h3>
          <ul className="mt-3 divide-y divide-emerald-950/5">
            {sampleLoans.slice(0, 4).map((loan) => (
              <li key={loan.id} className="py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-emerald-950">{loan.shg}</div>
                    <div className="truncate text-[11px] text-emerald-900/50">{loan.purpose} · {loan.id}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-tabular text-sm font-semibold text-emerald-900">
                      {formatINR(loan.principal)}
                    </div>
                    <StatusBadge status={loan.status} />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Upcoming EMIs table */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-emerald-950/10 bg-white" data-testid="upcoming-emis-card">
        <div className="flex items-center justify-between border-b border-emerald-950/8 px-5 py-4">
          <h3 className="font-display text-base font-semibold text-emerald-950">
            {t.dashboard.upcomingEmis} <SampleBadge className="ml-1 align-middle" />
          </h3>
          <span className="text-[11px] text-emerald-900/50">{sampleRepayments.length} entries · sample</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-emerald-900/5 text-left text-[11px] font-semibold uppercase tracking-wider text-emerald-900/60">
              <tr>
                <th className="px-5 py-2.5">Receipt ID</th>
                <th className="px-5 py-2.5">SHG</th>
                <th className="px-5 py-2.5">Date</th>
                <th className="px-5 py-2.5 text-right">Amount</th>
                <th className="px-5 py-2.5">Mode</th>
                <th className="px-5 py-2.5">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-emerald-950/5">
              {sampleRepayments.map((r) => (
                <tr key={r.id} className="hover:bg-emerald-50/40">
                  <td className="px-5 py-3 font-mono text-xs text-emerald-900/70">{r.id}</td>
                  <td className="px-5 py-3 font-medium text-emerald-950">{r.shg}</td>
                  <td className="px-5 py-3 text-emerald-900/70">{r.date}</td>
                  <td className="px-5 py-3 text-right font-mono-tabular font-semibold text-emerald-950">{formatINR(r.amount)}</td>
                  <td className="px-5 py-3 text-emerald-900/70">{r.mode}</td>
                  <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
